import { useEffect, useState } from 'react';

import { Redirect, Route, Switch, useHistory } from 'react-router-dom';

import { Alert, Box, Card, CloseButton, Heading, Page, Paragraph, Tabs } from '@zillow/constellation-10';

// // Components
import PaymentInfoModal from '../../components/modals/PaymentInfoModal';
import PaymentDuePage from '../PaymentDuePage';
import PaymentSettingsPage from '../PaymentSettingsPage';
import PaidInvoicesPage from '../PaidInvoicesPage';
import BillingPortalMaintenanceBanner from '../../components/BillingPortalMaintenanceBanner';

import billingUtils from '../../utils/billingUtils';

import { withErrorBoundary } from '../../components/withErrorBoundry';
import { InvoiceListProvider } from '../../providers/InvoiceListProvider';
import {
  AuthCheckWrapper,
  HeadWrapper,
  useRentalManagerUser,
  useRmwPaths,
  useRouter,
} from '@zg-rentals/rental-manager-env';
import type { History } from 'history';
import InvoiceSearchBox from '../../components/InvoiceSearchBox';
import { useAppBanner } from '../../hooks/useBanner';

const DEFAULT_PAGE_TITLE = 'Billing';

const TabList = {
  paid: 'paid',
  due: 'due',
  paymentSettings: 'paymentSettings',
};

const BillingPage = () => {
  const paths = useRmwPaths();
  const { user } = useRentalManagerUser();
  const router = useRouter();
  const { bannerState, setBannerState } = useAppBanner();
  const { displaySuccessMessage } = bannerState;
  const { canAccessBilling } = user.features;
  const isOnlyNucleusUser = billingUtils.isOnlyNucleusUser(user);
  const isSearchEnabled = !billingUtils.isOnlyNucleusUser(user);
  const [showPaymentInfoModal, setShowPaymentInfoModal] = useState(false);
  const history = useHistory<History>();

  useEffect(() => {
    return () => {
      setBannerState({ displaySuccessMessage: false });
    };
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- preserving existing behavior; not re-verified
  }, []);

  const loadTab = (tab: string) => {
    const url = `${paths.billingPage}/${tab}`;
    history.push(url);
  };

  const getActiveTab = () => {
    // Expecting path to be [baseUrl]/rental-manager/billing/*something*
    const tabName = router.location.pathname.split('/')[3];

    switch (tabName) {
      case paths.billingDue:
        return paths.billingDue;
      case paths.billingPaid:
        return paths.billingPaid;
      case 'paymentSettings':
        return 'paymentSettings';
      default:
        return paths.billingDue;
    }
  };

  const getSectionUrl = (section: string) => {
    const baseDetailsUrl = paths.billingPage;
    return `${baseDetailsUrl}/${section}([?].*)?`;
  };

  const renderChildRoutes = () => {
    const baseDetailsUrl = paths.billingPage;
    const childPathDue = getSectionUrl('due');
    const childPathPaid = getSectionUrl('paid');
    const childPathPaymentSettings = getSectionUrl('paymentSettings');
    return (
      <Switch>
        <Route
          render={() => {
            if (isOnlyNucleusUser) {
              return <Redirect to={`${baseDetailsUrl}/${paths.billingPaid}`} />;
            }
            return <Route component={PaymentDuePage} path={childPathDue} />;
          }}
          path={childPathDue}
        />
        <Route component={PaidInvoicesPage} path={childPathPaid} />
        <Route component={PaymentSettingsPage} path={childPathPaymentSettings} />
      </Switch>
    );
  };

  const renderSubNav = () => (
    <Box
      css={{
        marginTop: 'xs',
        marginBottom: 'xs',
      }}
    >
      <Tabs.Root defaultSelected={getActiveTab()} onSelectedChange={loadTab} aria-label="billing-portal-tabs">
        <Tabs.List>
          <Tabs.Tab value={TabList.due}>Payment due</Tabs.Tab>
          <Tabs.Tab value={TabList.paid}>Payment history</Tabs.Tab>
          <Tabs.Tab value={TabList.paymentSettings}>Payment settings</Tabs.Tab>
        </Tabs.List>
      </Tabs.Root>
    </Box>
  );

  const renderWidget = () => (isSearchEnabled ? <InvoiceSearchBox /> : undefined);

  const renderContent = () => {
    if (!canAccessBilling) {
      return (
        <Page.Root css={{ background: 'bg.screen.neutral' }}>
          <Page.Header>
            <Heading level={5}>{DEFAULT_PAGE_TITLE}</Heading>
          </Page.Header>
          <Page.Content>
            <Card aria-label="Billing" elevated={false}>
              <Box css={{ padding: 'lg' }}>
                <Paragraph>
                  Looking for invoices? If you manage multiple billing accounts, please log out of this account and try
                  a different one.
                </Paragraph>
              </Box>
            </Card>
          </Page.Content>
        </Page.Root>
      );
    }
    return (
      <Page.Root css={{ background: 'bg.screen.neutral' }}>
        <Page.Header>
          <Heading level={5}>{DEFAULT_PAGE_TITLE}</Heading>
          {renderWidget()}
        </Page.Header>
        <BillingPortalMaintenanceBanner />
        <Page.Content>
          {displaySuccessMessage && (
            <Alert
              isOpen={true}
              tone="success"
              onClose={() => setBannerState({ displaySuccessMessage: false })}
              closeButton={<CloseButton />}
            >
              Thank you for your payment!
            </Alert>
          )}
          <Card aria-label="Billing" elevated={false}>
            <Box css={{ marginBottom: '32px' }}>
              {!isOnlyNucleusUser && renderSubNav()}
              {renderChildRoutes()}
            </Box>
          </Card>
        </Page.Content>
      </Page.Root>
    );
  };

  return (
    <AuthCheckWrapper>
      <InvoiceListProvider>
        <div className="BillingPage">
          <HeadWrapper title={DEFAULT_PAGE_TITLE} />
          {renderContent()}
          {showPaymentInfoModal && <PaymentInfoModal onClose={() => setShowPaymentInfoModal(false)} />}
        </div>
      </InvoiceListProvider>
    </AuthCheckWrapper>
  );
};

export default withErrorBoundary(BillingPage);
